export type AsyncTask = () => Promise<unknown>

export class SimpleAsyncTaskQueue {
    private queue: AsyncTask[] = []
    private timeout: number | undefined = undefined
    private isRunning: boolean = false

    constructor(timeoutInMs?: number) {
        this.timeout = timeoutInMs
    }

    private enqueue = (task: AsyncTask) => {
        this.queue.push(task)
    }

    private dequeue = () => this.queue.shift()

    private processTask = async (task: AsyncTask) => {
        const taskPromise = task()
        let taskTimedOut = false
        let taskToWaitOn = taskPromise
        if (this.timeout) {
            const delayTask = new Promise((resolve) =>
                setTimeout(() => {
                    taskTimedOut = true
                    resolve(undefined)
                }, this.timeout),
            )
            taskToWaitOn = Promise.race([taskPromise, delayTask]).finally(
                () => {
                    if (taskTimedOut) {
                        console.warn('Task timed out; proceeding to next task.')
                    }
                },
            )
        }
        try {
            await taskToWaitOn
        } catch (error) {
            console.error('Queued task failed with error', error)
        }
    }

    private processQueue = async () => {
        if (this.isRunning) {
            return
        }
        this.isRunning = true
        while (this.queue.length > 0) {
            const task = this.dequeue()
            if (task) {
                try {
                    await this.processTask(task)
                } catch (error) {
                    console.error('Processing task failed with error: ', error)
                }
            }
        }
        this.isRunning = false
    }

    isEmpty() {
        return this.queue.length === 0
    }

    addTaskToQueue(task: AsyncTask) {
        this.enqueue(task)
        this.processQueue()
    }
}
